// Library service — talks to /api/library with pagination + partial-accept support

import type { AuthorizedMediaItem, DriveAuthSession } from '../types';

const API_BASE = '/api';

const authHeaders = (session: DriveAuthSession) => ({
  Authorization: `Bearer ${session.idToken || session.accessToken}`,
  'x-access-token': session.accessToken,
  'Content-Type': 'application/json',
});

const chunk = <T,>(arr: T[], size: number): T[][] => {
  const chunks: T[][] = [];
  for (let i = 0; i < arr.length; i += size) chunks.push(arr.slice(i, i + size));
  return chunks;
};

export interface UpsertResponse {
  items: AuthorizedMediaItem[];
  added: number;
  skipped: number;
  total: number;
  limitExceeded?: boolean;
  limit?: number;
  tier?: string;
  message?: string;
}

export const libraryService = {
  /**
   * Load entire library (backward compat).
   * Uses ?all=true to get everything in one call.
   */
  async getLibrary(session: DriveAuthSession): Promise<AuthorizedMediaItem[]> {
    try {
      const response = await fetch(`${API_BASE}/library?all=true`, {
        method: 'GET',
        headers: authHeaders(session),
      });

      if (!response.ok) {
        if ([400, 401, 403, 404, 500, 502, 503].includes(response.status)) return [];
        throw new Error('Failed to fetch server library.');
      }

      const data = await response.json();
      return data.items || [];
    } catch (error) {
      console.warn('Server library unavailable; continuing with empty library.', error);
      return [];
    }
  },

  /**
   * Paginated library load — returns items progressively.
   */
  async getLibraryPaginated(
    session: DriveAuthSession,
    onPage: (items: AuthorizedMediaItem[], total: number, hasMore: boolean) => void,
    pageSize: number = 1000
  ): Promise<AuthorizedMediaItem[]> {
    let cursor: string | null = null;
    const all: AuthorizedMediaItem[] = [];

    do {
      const url = new URL(`${window.location.origin}${API_BASE}/library`);
      url.searchParams.set('limit', String(pageSize));
      if (cursor) url.searchParams.set('cursor', cursor);

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: authHeaders(session),
      });

      if (!response.ok) break;
      const data = await response.json();

      all.push(...(data.items || []));
      cursor = data.cursor;
      onPage(all, data.total, data.hasMore);
    } while (cursor);

    return all;
  },

  /**
   * Upsert library items — handles partial accept from limit enforcement.
   * Falls back to local-only mode when API is unavailable (404).
   */
  async upsertLibrary(
    session: DriveAuthSession,
    items: Omit<AuthorizedMediaItem, 'addedAt'>[]
  ): Promise<UpsertResponse> {
    if (items.length === 0) {
      return { items: [], added: 0, skipped: 0, total: 0 };
    }

    let latest: UpsertResponse = { items: [], added: 0, skipped: 0, total: 0 };

    for (const part of chunk(items, 500)) {
      try {
        const response = await fetch(`${API_BASE}/library`, {
          method: 'POST',
          headers: authHeaders(session),
          body: JSON.stringify({ items: part }),
        });

        // Handle 404 gracefully - API not available, work in local-only mode
        if (response.status === 404) {
          console.warn('Server library API not available (404); working in local-only mode.');
          // Convert input items to AuthorizedMediaItem format with addedAt
          const localItems: AuthorizedMediaItem[] = part.map(item => ({
            ...item,
            addedAt: new Date().toISOString(),
          }));
          latest = {
            items: [...latest.items, ...localItems],
            added: latest.added + part.length,
            skipped: latest.skipped,
            total: latest.total + part.length,
          };
          continue;
        }

        if (!response.ok) {
          let message = 'Failed to update server library.';
          try {
            const data = await response.json();
            message = data?.message || data?.error || message;
          } catch {}
          throw new Error(message);
        }

        const data = await response.json();
        latest = {
          items: data.items || latest.items,
          added: (latest.added || 0) + (data.added || 0),
          skipped: (latest.skipped || 0) + (data.skipped || 0),
          total: data.total || latest.total,
          limitExceeded: data.limitExceeded,
          limit: data.limit,
          tier: data.tier,
          message: data.message,
        };

        // If limit hit, stop sending more chunks
        if (data.limitExceeded) break;
      } catch (error: any) {
        // Network errors or other failures - fall back to local-only mode
        if (error.message?.includes('Failed to fetch') || error.message?.includes('NetworkError')) {
          console.warn('Server library API unavailable; working in local-only mode.', error);
          const localItems: AuthorizedMediaItem[] = part.map(item => ({
            ...item,
            addedAt: new Date().toISOString(),
          }));
          latest = {
            items: [...latest.items, ...localItems],
            added: latest.added + part.length,
            skipped: latest.skipped,
            total: latest.total + part.length,
          };
          continue;
        }
        throw error;
      }
    }

    return latest;
  },

  async removeFromLibrary(session: DriveAuthSession, fileIds: string[]) {
    const response = await fetch(`${API_BASE}/library`, {
      method: 'DELETE',
      headers: authHeaders(session),
      body: JSON.stringify({ fileIds }),
    });

    if (!response.ok) {
      throw new Error('Failed to delete items from server library.');
    }
  },
};
